import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Check, Plus } from "lucide-react";
import { Spinner } from "@/components/ui-bits";
import { useLang } from "@/i18n/LangContext";

const now = new Date();
const pad = (n) => String(n).padStart(2, "0");
const toLocalInput = (d) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export default function MantenimientoForm() {
  const nav = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const { t } = useLang();
  const editing = !!id;
  const isAdmin = user?.role === "admin";
  const empEdit = editing && !isAdmin;

  const [embs, setEmbs] = useState([]);
  const [users, setUsers] = useState([]);
  const [servs, setServs] = useState([]);
  const [clientes, setClientes] = useState([]);
  const def = new Date(); def.setHours(9,0,0,0);
  const [form, setForm] = useState({
    embarcacion_id: "",
    empleado_ids: [],
    services: [], // {service_id, realizado}
    start_at: toLocalInput(def),
    observaciones: "",
    status: "programado",
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [e, s, c] = await Promise.all([api.get("/embarcaciones"), api.get("/servicios"), api.get("/clientes")]);
        setEmbs(e.data); setServs(s.data); setClientes(c.data);
        const uRes = await api.get("/users").catch(() => null);
        if (uRes) setUsers(uRes.data.filter((x) => x.active));
        if (editing) {
          const { data } = await api.get(`/mantenimientos/${id}`);
          setForm({
            embarcacion_id: data.embarcacion_id,
            empleado_ids: data.empleado_ids || [],
            services: data.services || [],
            start_at: data.start_at?.slice(0, 16) || toLocalInput(def),
            observaciones: data.observaciones || "",
            status: data.status,
          });
        } else if (!isAdmin) {
          setForm((f) => ({ ...f, empleado_ids: [user.id] }));
        }
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, [id]);

  const selectedEmb = embs.find((e) => e.id === form.embarcacion_id);
  const selectedCliente = selectedEmb ? clientes.find((c) => c.id === selectedEmb.cliente_id) : null;

  const toggleEmp = (uid) => setForm((f) => ({
    ...f, empleado_ids: f.empleado_ids.includes(uid)
      ? f.empleado_ids.filter((x) => x !== uid)
      : [...f.empleado_ids, uid],
  }));

  const toggleSvc = (sid) => setForm((f) => {
    const exists = f.services.find((s) => s.service_id === sid);
    return {
      ...f,
      services: exists
        ? f.services.filter((s) => s.service_id !== sid)
        : [...f.services, { service_id: sid, realizado: false }],
    };
  });

  const toggleRealizado = (sid) => setForm((f) => ({
    ...f,
    services: f.services.map((s) => s.service_id === sid ? { ...s, realizado: !s.realizado } : s),
  }));

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const payload = {
        ...form,
        start_at: `${form.start_at}:00`,
      };
      if (empEdit) {
        await api.put(`/mantenimientos/${id}/servicios`, { services: form.services });
        nav(`/trabajos/${id}`);
        return;
      }
      if (!isAdmin) payload.empleado_ids = [user.id];
      if (editing) {
        await api.patch(`/mantenimientos/${id}`, payload);
        nav(`/trabajos/${id}`);
      } else {
        const { data } = await api.post("/mantenimientos", payload);
        nav(`/trabajos/${data.id}`);
      }
    } catch (ex) { setErr(fmtErr(ex)); } finally { setSaving(false); }
  };

  if (loading) return <Spinner label={t("common.loading")} />;
  const cols = isAdmin ? "grid-cols-[auto_1fr_auto_auto]" : "grid-cols-[auto_1fr_auto]";

  return (
    <div className="px-5 pt-8 pb-32 sns-fade-up max-w-5xl mx-auto" data-testid="form-mantenimiento">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => nav(-1)} className="w-11 h-11 rounded-full bg-[var(--sky)] text-[var(--sky-text)] flex items-center justify-center active:scale-95 transition-transform" data-testid="btn-back-mform"><ArrowLeft size={20} /></button>
        <h1 className="sns-title text-2xl">{editing ? t("mform.edit") : t("mform.new")}</h1>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="md:grid md:grid-cols-2 md:gap-4 space-y-4 md:space-y-0">
          <Field label={t("mform.property")}>
            <select value={form.embarcacion_id} onChange={(e) => setForm({ ...form, embarcacion_id: e.target.value })} required disabled={empEdit} className={INPUT} data-testid="select-embarcacion">
              <option value="">{t("common.select")}</option>
              {embs.map((x) => <option key={x.id} value={x.id}>{x.name} ({x.matricula || x.puerto || "—"})</option>)}
            </select>
            {selectedCliente && isAdmin && (
              <div className="mt-2 text-xs flex items-center gap-2 px-3 py-2 rounded-xl bg-[var(--sky)] text-[var(--sky-text)]" data-testid="mant-cliente-info">
                <span className="font-bold">{t("mform.client_label")}</span>
                <span>{selectedCliente.empresa || selectedCliente.nombre}</span>
                {selectedCliente.tel1 && <span className="opacity-75">· {selectedCliente.tel1}</span>}
              </div>
            )}
          </Field>
          <Field label={t("mform.status")}>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={INPUT} disabled={!isAdmin} data-testid="select-status">
              <option value="programado">{t("status.programado")}</option>
              <option value="en_curso">{t("status.en_curso")}</option>
              <option value="finalizado">{t("status.finalizado")}</option>
              <option value="retraso">{t("status.retraso")}</option>
            </select>
          </Field>
          <Field label={t("mform.start_dt")}>
            <input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} required disabled={empEdit} className={INPUT} data-testid="input-start-at" />
          </Field>
        </div>

        <Field label={`${t("mform.workers")} (${form.empleado_ids.length})`}>
          {isAdmin ? (
            <div className="sns-card p-3 grid md:grid-cols-2 gap-2 border-[#BFD4F5]">
              {users.map((u) => (
                <label key={u.id} className={`flex items-center gap-3 p-2 rounded-xl cursor-pointer border transition-colors ${
                  form.empleado_ids.includes(u.id) ? "bg-orange-50 border-[var(--coral)]" : "border-transparent hover:bg-[var(--sky)]"
                }`} data-testid={`emp-chk-${u.id}`}>
                  <input type="checkbox" checked={form.empleado_ids.includes(u.id)} onChange={() => toggleEmp(u.id)} className="w-5 h-5 accent-[var(--coral)]" />
                  <div className="w-8 h-8 rounded-full bg-[var(--sky-text)] text-white text-sm font-bold flex items-center justify-center">{u.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate text-[var(--ink)]">{u.name}</div>
                    <div className="text-xs text-[var(--sky-text)] truncate">{u.role}</div>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className={`${INPUT} flex items-center gap-2`}>
              <span className="sns-pill bg-[var(--coral)] text-white">{t("mform.you")}</span>
              {user?.name}
            </div>
          )}
        </Field>

        <Field label={`${t("mform.services")} (${form.services.length})`}>
          <div className="sns-card overflow-hidden border-[#BFD4F5]">
            <div className={`grid ${cols} gap-3 px-4 py-3 bg-[var(--sky)] border-b border-[#BFD4F5] text-xs font-bold text-[var(--sky-text)] uppercase tracking-wider`}>
              <div>{t("mform.col.assign")}</div>
              <div>{t("mform.col.service")}</div>
              {isAdmin && <div className="text-right">{t("mform.col.price")}</div>}
              <div>{t("mform.col.done")}</div>
            </div>
            {servs.map((s) => {
              const sel = form.services.find((x) => x.service_id === s.id);
              return (
                <div key={s.id} className={`grid ${cols} gap-3 items-center px-4 py-2.5 border-b border-[var(--border)] last:border-0 transition-colors ${sel ? "bg-orange-50" : ""}`} data-testid={`svc-row-${s.id}`}>
                  <input type="checkbox" checked={!!sel} onChange={() => toggleSvc(s.id)} className="w-5 h-5 accent-[var(--coral)]" data-testid={`svc-assign-${s.id}`} />
                  <div className="min-w-0">
                    <div className="font-medium truncate text-[var(--ink)]">{s.code && <span className="font-bold text-[var(--sky-text)]">{s.code} · </span>}{s.name}</div>
                    {s.especial && <span className="text-[10px] font-bold text-[var(--coral)]">{t("mform.special")}</span>}
                  </div>
                  {isAdmin && <div className="text-sm font-semibold text-[var(--ink)] tabular-nums" data-testid={`svc-price-${s.id}`}>{s.price ? `${s.price}€` : "—"}</div>}
                  <label className={`w-8 h-8 rounded-lg border-2 flex items-center justify-center cursor-pointer transition-all ${
                    sel?.realizado ? "bg-[var(--coral)] border-[var(--coral)]" : "border-[#BFD4F5] bg-white"
                  } ${!sel ? "opacity-30 pointer-events-none" : ""}`}>
                    <input type="checkbox" checked={!!sel?.realizado} onChange={() => toggleRealizado(s.id)} className="hidden" data-testid={`svc-done-${s.id}`} />
                    {sel?.realizado && <Check size={16} className="text-white" />}
                  </label>
                </div>
              );
            })}
            {isAdmin && <NewServiceInline onCreated={(s) => { setServs((l) => [...l, s]); toggleSvc(s.id); }} />}
          </div>
        </Field>

        <Field label={isAdmin ? t("mform.obs") : t("mform.obs.admin_only")}>
          {isAdmin ? (
            <textarea
              value={form.observaciones}
              onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
              rows={4}
              className={`${INPUT} resize-none`}
              data-testid="textarea-observaciones"
              placeholder={t("mform.obs.placeholder")}
            />
          ) : form.observaciones ? (
            <div className={`${INPUT} text-[var(--ink)] whitespace-pre-wrap min-h-[80px]`} data-testid="obs-readonly">
              {form.observaciones}
            </div>
          ) : (
            <div className="text-xs text-slate-400 italic px-3 py-2">
              {t("mform.obs.empty")}
            </div>
          )}
        </Field>

        {err && <div className="text-sm text-red-600 bg-red-50 rounded-xl p-3">{err}</div>}

        <button disabled={saving} className="sns-btn sns-btn-accent w-full py-4 text-base" data-testid="btn-save-mantenimiento">
          {saving ? t("common.saving") : (editing ? t("mform.save_edit") : t("mform.save_new"))}
        </button>
      </form>
    </div>
  );
}

const INPUT = "sns-input !bg-white !border-[#BFD4F5] text-[var(--ink)] focus:!border-[var(--coral)] disabled:opacity-60 disabled:cursor-not-allowed";

const NewServiceInline = ({ onCreated }) => {
  const [open, setOpen] = useState(false);
  const [d, setD] = useState({ name: "", code: "", price: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const save = async () => {
    if (!d.name.trim()) { setErr("Pon un nombre al servicio"); return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/servicios", { name: d.name.trim(), code: d.code.trim(), price: Number(d.price) || 0 });
      onCreated(data);
      setD({ name: "", code: "", price: "" }); setOpen(false);
    } catch (e) { setErr(fmtErr(e)); } finally { setBusy(false); }
  };
  if (!open) return (
    <button type="button" onClick={() => setOpen(true)} className="w-full px-4 py-3 text-sm font-bold text-[var(--coral)] hover:bg-orange-50 flex items-center gap-2" data-testid="btn-new-service-inline">
      <Plus size={16} /> Crear nuevo servicio
    </button>
  );
  return (
    <div className="p-4 bg-orange-50 space-y-2" data-testid="new-service-inline" onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}>
      <div className="grid grid-cols-[80px_1fr_90px] gap-2">
        <input value={d.code} onChange={(e) => setD({ ...d, code: e.target.value })} placeholder="Código" className={INPUT} data-testid="new-svc-code" />
        <input value={d.name} onChange={(e) => setD({ ...d, name: e.target.value })} placeholder="Nombre del servicio" className={INPUT} data-testid="new-svc-name" autoFocus />
        <input value={d.price} onChange={(e) => setD({ ...d, price: e.target.value })} placeholder="Precio €" type="number" step="0.01" className={INPUT} data-testid="new-svc-price" />
      </div>
      {err && <div className="text-xs text-red-600">{err}</div>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={() => setOpen(false)} className="sns-btn sns-btn-ghost bg-white text-sm px-4 py-2">Cancelar</button>
        <button type="button" onClick={save} disabled={busy} className="sns-btn sns-btn-accent text-sm px-4 py-2 disabled:opacity-60" data-testid="new-svc-save">{busy ? "Creando…" : "Crear y añadir"}</button>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="flex items-center gap-2 text-sm font-bold mb-2 text-[var(--sky-text)]">
      <span className="w-1.5 h-4 rounded-full bg-[var(--coral)]" />{label}
    </label>
    {children}
  </div>
);
